import React from "react";
import moment from "moment";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { brand } from "../../theme/businessFlowsTheme";

const WIRE_FORMAT = "YYYY-MM-DD";

// Common choices, so an admin rarely has to page through a calendar.
const SHORTCUTS = [
  { label: "In 14 days", days: 14 },
  { label: "In 30 days", days: 30 },
  { label: "In 3 months", days: 90 },
  { label: "In a year", days: 365 },
];

// A date field that reads and writes "YYYY-MM-DD", with the calendar styled
// like the rest of the console. The calendar renders outside the .bfx scope,
// so its paper carries the class itself.
export default function DateField({ label, value, onChange, disabled, helperText, minDate, sx }) {
  const parsed = value ? moment(value, WIRE_FORMAT, true) : null;
  const current = parsed && parsed.isValid() ? parsed : null;
  const set = (next) => onChange(next && next.isValid() ? next.format(WIRE_FORMAT) : "");

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DatePicker
        label={label}
        value={current}
        disabled={disabled}
        minDate={minDate ? moment(minDate, WIRE_FORMAT) : undefined}
        format="D MMM YYYY"
        onChange={set}
        // Always the desktop calendar: the console is an admin tool.
        desktopModeMediaQuery="@media (min-width: 0px)"
        slots={{
          // Quick choices above the calendar, then the calendar itself.
          toolbar: () =>
            disabled ? null : (
              <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {SHORTCUTS.map((shortcut) => (
                    <Button
                      key={shortcut.label}
                      size="small"
                      onClick={() => set(moment().add(shortcut.days, "days"))}
                      sx={{ borderRadius: 2, fontSize: "0.72rem", px: 1, minWidth: 0 }}
                    >
                      {shortcut.label}
                    </Button>
                  ))}
                </Stack>
              </Box>
            ),
        }}
        slotProps={{
          textField: { size: "small", fullWidth: true, helperText, sx },
          actionBar: { actions: disabled ? [] : ["clear", "today", "accept"] },
          desktopPaper: {
            className: "bfx",
            sx: {
              borderRadius: 4,
              border: `1px solid ${brand.line}`,
              boxShadow: "0 18px 40px rgba(26,26,46,0.16)",
              "& .MuiPickersDay-root": { fontWeight: 600 },
              "& .MuiPickersDay-root.Mui-selected": {
                bgcolor: brand.ink,
                color: brand.signal,
                "&:hover, &:focus": { bgcolor: brand.inkSoft },
              },
              "& .MuiPickersDay-today:not(.Mui-selected)": { borderColor: brand.signalDeep },
              "& .MuiDayCalendar-weekDayLabel": { fontWeight: 700, color: "text.secondary" },
              "& .MuiPickersCalendarHeader-label": { fontWeight: 700 },
              "& .MuiDialogActions-root .MuiButton-root": { fontWeight: 600 },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}
